import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import type { Toast } from "primereact/toast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import AppToast from "../components/AppToast";
import { usePageTitle } from "../components/PageTitle";
import { apiErrorMessage } from "../lib/apiError";
import type { JoinInfoDto, JoinResultDto } from "../types/api";
import { applyDirection } from "../lib/direction";

// For parsing local storage
type Player = { id: string; name: string };
// Mapped backend fields  (id/name)
type Team = { id: string; name: string; game_code: string };

function safeParse<T>(raw: string | null): T | null {
  try {
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export default function JoinPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("app.joinTeam"));

  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const [teamInfo, setTeamInfo] = useState<Team | null>(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  /* Memoised so the lookup effect can depend on it: an unmemoised callback
     would change identity every render and refetch in a loop. */
  const showError = useCallback(
    (message: string) => {
      toast.current?.show({
        severity: "error",
        summary: t("adminToasts.error"),
        detail: message,
        life: 5000,
      });
    },
    [t]
  );

  // Check Session - Redirect/Fetch Team
  useEffect(() => {
    if (!code) return;

    const API = import.meta.env.VITE_API_BASE ?? "/api";

    // Fetch Team Info (Regular join)
    async function fetchTeamInfo() {
      setLoading(true);
      const res = await fetch(`${API}/public/join/${code}`);
      const txt = await res.text();
      let data: JoinInfoDto | null = null;
      try {
        data = txt ? JSON.parse(txt) : null;
      } catch {
        /* Non-JSON body: handled as a failed lookup below. */
      }

      setLoading(false);

      /* A 200 without these fields is a malformed link as far as the player
         is concerned, so treat it the same as an error rather than rendering
         a card with blank values. */
      if (!res.ok || !data?.team_id || !data.team_name || !data.game_code) {
        showError(apiErrorMessage({ data }, t));
        return;
      }
      setTeamInfo({
        id: data.team_id,
        name: data.team_name,
        game_code: data.game_code,
      });
    }

    const me = safeParse<Player>(localStorage.getItem("player"));
    const team = safeParse<Team>(localStorage.getItem("team"));

    if (me?.id && me.name && team?.id && team.name && team.game_code) {
      // For local, start verification
      (async () => {
        const res = await fetch(`${API}/public/join/${code}`);
        const data: JoinInfoDto | null = res.ok
          ? await res.json().catch(() => null)
          : null;

        if (res.ok && data?.game_code) {
          // Check team/game code match the link's info
          if (team.id === data.team_id && team.game_code === data.game_code) {
            nav(`/play/${data.game_code}`);
            return;
          }
        }
        await fetchTeamInfo();
      })();
    } else {
      // No local session found
      fetchTeamInfo();
    }
  }, [code, nav, showError, t]);

  // Submit player name to join the team
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!code) return;

    setSubmitting(true);
    try {
      const API = import.meta.env.VITE_API_BASE ?? "/api";
      const res = await fetch(`${API}/public/join/${code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data: JoinResultDto | null = await res.json().catch(() => null);

      if (!res.ok || !data) {
        showError(apiErrorMessage({ data }, t));
        return;
      }

      // Save the game_code as part of the team object.
      localStorage.setItem("player", JSON.stringify(data.player));
      localStorage.setItem(
        "team",
        JSON.stringify({ ...data.team, game_code: data.game_code })
      );
      nav(`/play/${data.game_code}`);
    } finally {
      setSubmitting(false);
    }
  }

  // Loading: a skeleton shaped like the card that is coming, rather than a
  // spinner that tells you nothing about what to expect.
  if (loading) {
    return (
      <AppShell width="center">
        <div className="auth-card">
          <Skeleton width="7rem" height="0.8rem" />
          <Skeleton width="65%" height="2rem" className="mt-3" />
          <Skeleton width="90%" height="1rem" className="mt-3" />
          <Skeleton width="100%" height="3rem" className="mt-5" />
          <Skeleton width="100%" height="2.8rem" className="mt-3" />
        </div>
      </AppShell>
    );
  }

  // Error/Invalid flow
  if (!teamInfo) {
    return (
      <AppShell width="center">
        <AppToast ref={toast} position="top-right" />
        <section className="auth-card rise" style={{ textAlign: "center" }}>
          <span
            className="empty__icon"
            style={{
              margin: "0 auto",
              background: "var(--danger-soft)",
              color: "var(--danger)",
            }}
          >
            <i className="pi pi-link" aria-hidden="true" />
          </span>
          <h1 className="auth-card__title" style={{ marginTop: "var(--space-4)" }}>
            {t("joinTeam.linkInvalid")}
          </h1>
          <p className="auth-card__text">{t("joinTeam.linkInvalidTxt")}</p>
          <Button
            label={t("joinTeam.goHome")}
            icon="pi pi-arrow-left"
            onClick={() => nav("/")}
            className="w-full"
            style={{ marginTop: "var(--space-5)" }}
          />
        </section>
      </AppShell>
    );
  }

  // Main Join Form
  return (
    <AppShell width="center">
      <AppToast ref={toast} position="top-right" />
      <section className="auth-card rise">
        <p className="auth-card__eyebrow">{t("app.joinTeam")}</p>
        <h1 className="auth-card__title">{teamInfo.name}</h1>
        <p className="auth-card__text">{t("joinTeam.joinTxt")}</p>

        <form onSubmit={submit}>
          <div className="field">
            <label className="field__label" htmlFor="name">
              {t("joinTeam.yourName")}
            </label>
            <InputText
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              minLength={2}
              required
              autoComplete="name"
              placeholder={t("joinTeam.joinholder")}
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            style={{ marginTop: "var(--space-5)" }}
            disabled={name.trim().length < 2}
            loading={submitting}
            label={t("joinTeam.joinBtn")}
            icon="pi pi-arrow-right"
            iconPos="right"
          />
        </form>
      </section>
    </AppShell>
  );
}
